import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useSelector } from 'APP/Hooks/redux'
import PushNotification from 'react-native-push-notification'
import { Divider } from 'react-native-elements'
import styled, { useTheme } from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'

import Config from 'APP/Config'

import Analytics from 'APP/Services/Analytics'
import I18n from 'APP/I18n'
import { isWeb } from 'APP/Helpers/checkPlatform'

import NotificationsScreenIcon from 'APP/Images/Account/notifications-screen-icon.svg'

import VirtualCareAccountBanner from 'APP/Components/VirtualCareAccountBanner'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import LogoHeader from 'APP/Components/LogoHeader'
import ActivityIndicator from 'APP/Components/ActivityIndicator'
import { ResponsiveScrollView, ResponsiveView } from 'APP/Converse/Layout'
import { NotificationsNotAllowed } from './NotificationsNotAllowed'
import { NotificationCenter } from './NotificationsCenter'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`
const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const NotificationsScreen = () => {
  const theme = useTheme()
  const { isActive } = useSelector((state) => state.appSession)
  const { preferences } = useSelector((state) => state.notifications)
  const { eligibleServices } = useSelector((state) => state.patient.profile)
  const hasWellnessChallenges = eligibleServices?.wellness_challenges
  const hasWBI = eligibleServices?.wellbeing_index_assessment
  const [allowNotifications, setAllowNotifications] = useState(undefined)

  const eligiblePreferences = useMemo(
    () =>
      Object.entries(preferences?.data || {}).reduce((nextEligiblePreferences, [key, value]) => {
        let isEligible = true
        switch (key) {
          case 'challengeUpdates':
            isEligible = hasWellnessChallenges
            break
          case 'wbiReminders':
            isEligible = hasWBI
            break
        }

        if (isEligible) nextEligiblePreferences[key] = value

        return nextEligiblePreferences
      }, {}),
    [preferences, hasWellnessChallenges, hasWBI]
  )

  const checkPermissions = useCallback(() => {
    if (isWeb()) {
      setAllowNotifications(true)
      return
    }

    PushNotification.checkPermissions((result) => {
      setAllowNotifications((previousState) => {
        const permission = result?.alert

        if (permission !== previousState) {
          const screenName = permission ? 'NOTIFICATION PREFERENCES' : 'ENABLE PUSH'

          Analytics.trackScreen(screenName)
        }

        return result?.alert
      })
    })
  }, [])

  // Re-check notification permissions when the app is backgrounded or foregrounded
  useEffect(() => {
    checkPermissions()
  }, [isActive, checkPermissions])

  return (
    <NoticeBarContainer>
      <ScreenContainer>
        <ResponsiveScrollView>
          <ContentContainer>
            <LogoHeader
              source={NotificationsScreenIcon}
              title={I18n.t('NotificationsScreen.header.title')}
              subtitle={I18n.t('NotificationsScreen.header.subtitle')}
            />
            <Divider />

            {allowNotifications === undefined && (
              <ActivityIndicator
                size="large"
                color={theme.colors.text}
                testID="LOADING_INDICATOR"
              />
            )}

            {allowNotifications === false && (
              <NotificationsNotAllowed onPermissionsChanged={checkPermissions} />
            )}

            {allowNotifications && Config.HAS_CUSTOM_ACCOUNT_BANNER && <VirtualCareAccountBanner />}

            {allowNotifications && <NotificationCenter preferences={eligiblePreferences} />}
          </ContentContainer>
        </ResponsiveScrollView>
      </ScreenContainer>
    </NoticeBarContainer>
  )
}

export default NotificationsScreen
